import { MEMBER_ROLE } from "../account/account.js";
import { ACCOUNT_ROLE } from "../account/account-membership.js";
// ---------------------------------------------------------------------------------------
// The two closed capability unions (design §27.5.2, §27.5.3 — the fixture's order)
// ---------------------------------------------------------------------------------------
/** Account capabilities, in the fixture's (and design §27.5.2's) order. */
export const ACCOUNT_CAPABILITY = [
    "account.profile.read",
    "account.workspace_metadata.read",
    "account.profile.write",
    "account.workspace.create",
    "account.workspace.manage",
    "account.workspace_access.grant",
    "account_membership.read",
    "account_membership.invite",
    "account_membership.role_change",
    "account_membership.remove",
    "account.ownership.transfer",
    "account.close",
    "account.finance.read",
    "account.finance.manage",
    "account.billing_delegate.manage",
    "account.security.read",
    "account.sign_in_policy.write",
    "account.audit.read",
    "account.audit.financial_read",
    "account.api_key.manage",
    "account.resource_share.read",
];
/** Workspace capabilities, in the fixture's (and design §27.5.3's) order. */
export const WORKSPACE_CAPABILITY = [
    "analytics.read",
    "billing.read",
    "conversation.read",
    "transcript.read",
    "caller_detail.read",
    "recording.play",
    "voice_agent.write",
    "agent_document.write",
    "channel_binding.write",
    "webhook_endpoint.manage",
    "campaign.operate",
    "contact_list.manage",
    "export.conversation",
    "export.campaign",
    "do_not_call_entry.add",
    "do_not_call_entry.remove",
    "membership.read",
    "membership.invite",
    "membership.role_change",
    "membership.remove",
    "api_key.manage",
    "api_key.integration_manage",
    "spend_cap.write",
    "resource_share.publish",
    "resource_share.accept",
    "workspace.lifecycle",
    "audit_entry.read",
    "compliance.operator_access",
    "person_data_request.create",
    "compliance.consent_line",
    // ADDED 2026-10-05 (plan-014 INT, owner answer 38): order-book refunds, Owner and Admin only.
    "customer_order.refund",
];
const ACCOUNT_CAPABILITY_SET = new Set(ACCOUNT_CAPABILITY);
const WORKSPACE_CAPABILITY_SET = new Set(WORKSPACE_CAPABILITY);
/** Whether a value is one of the account capabilities. */
export function isAccountCapability(value) {
    return typeof value === "string" && ACCOUNT_CAPABILITY_SET.has(value);
}
/** Whether a value is one of the workspace capabilities. */
export function isWorkspaceCapability(value) {
    return typeof value === "string" && WORKSPACE_CAPABILITY_SET.has(value);
}
// ---------------------------------------------------------------------------------------
// The two matrices (fixture `account` and `workspace`, restated cell by cell)
// ---------------------------------------------------------------------------------------
/** Account capability → the account roles holding it, in `ACCOUNT_ROLE` order. */
export const ACCOUNT_CAPABILITY_ROLE = Object.freeze({
    "account.profile.read": ["owner", "admin", "finance", "viewer"],
    "account.workspace_metadata.read": ["owner", "admin", "finance", "viewer"],
    "account.profile.write": ["owner", "admin"],
    "account.workspace.create": ["owner", "admin"],
    "account.workspace.manage": ["owner", "admin"],
    "account.workspace_access.grant": ["owner", "admin"],
    "account_membership.read": ["owner", "admin"],
    "account_membership.invite": ["owner", "admin"],
    "account_membership.role_change": ["owner", "admin"],
    "account_membership.remove": ["owner", "admin"],
    "account.ownership.transfer": ["owner"],
    "account.close": ["owner"],
    "account.finance.read": ["owner", "admin", "finance"],
    "account.finance.manage": ["owner", "admin", "finance"],
    "account.billing_delegate.manage": ["owner", "admin", "finance"],
    "account.security.read": ["owner", "admin"],
    "account.sign_in_policy.write": ["owner", "admin"],
    "account.audit.read": ["owner", "admin"],
    "account.audit.financial_read": ["owner", "admin", "finance"],
    "account.api_key.manage": ["owner", "admin"],
    "account.resource_share.read": ["owner", "admin"],
});
/**
 * Workspace capability → the member roles holding it, in `MEMBER_ROLE` order. Member keeps every
 * grant it held before plan-014 (design §27.5.3; `audit_entry.read` as amended by §27.23.9).
 */
export const WORKSPACE_CAPABILITY_ROLE = Object.freeze({
    "analytics.read": ["owner", "admin", "member", "viewer", "developer", "campaign_manager"],
    "billing.read": ["owner", "admin", "member", "viewer", "developer", "campaign_manager", "billing"],
    "conversation.read": ["owner", "admin", "member", "viewer", "developer", "campaign_manager"],
    // AMENDED 2026-10-05 (plan-014 INT, owner answer 37): a Viewer reads no transcript (review #133 kept).
    "transcript.read": ["owner", "admin", "member", "developer", "campaign_manager"],
    "caller_detail.read": ["owner", "admin"],
    "recording.play": ["owner", "admin"],
    "voice_agent.write": ["owner", "admin", "member", "developer"],
    "agent_document.write": ["owner", "admin", "member", "developer"],
    "channel_binding.write": ["owner", "admin", "member", "developer"],
    "webhook_endpoint.manage": ["owner", "admin", "member", "developer"],
    "campaign.operate": ["owner", "admin", "member", "campaign_manager"],
    "contact_list.manage": ["owner", "admin", "member", "campaign_manager"],
    "export.conversation": ["owner", "admin", "member"],
    "export.campaign": ["owner", "admin", "member", "campaign_manager"],
    "do_not_call_entry.add": ["owner", "admin", "member", "campaign_manager"],
    "do_not_call_entry.remove": ["owner", "admin", "member"],
    "membership.read": ["owner", "admin", "member", "viewer"],
    "membership.invite": ["owner", "admin"],
    "membership.role_change": ["owner", "admin"],
    "membership.remove": ["owner", "admin"],
    "api_key.manage": ["owner", "admin"],
    "api_key.integration_manage": ["owner", "admin", "developer"],
    "spend_cap.write": ["owner", "admin"],
    "resource_share.publish": ["owner", "admin"],
    "resource_share.accept": ["owner", "admin"],
    "workspace.lifecycle": ["owner"],
    "audit_entry.read": ["owner", "admin", "member"],
    "compliance.operator_access": ["owner", "admin"],
    "person_data_request.create": ["owner", "admin"],
    "compliance.consent_line": ["owner", "admin"],
    "customer_order.refund": ["owner", "admin"],
});
/**
 * ADDED 2026-10-09 (ADR 0041, superseding ADR 0033 in part; owner decision of 2026-10-09): the workspace capabilities
 * an ACCOUNT role holds on EVERY workspace of its account, with or without a role in that workspace (fixture
 * `workspace_by_account_role`). The organization's Owner and Admin see each workspace's people and archive, restore
 * or delete it — and nothing else: no operational content (fixture `operational_content_requires_workspace_role`
 * still holds) and no membership change (inviting, changing a role and removing stay with the workspace's own Owners
 * and Admins; an organization Owner or Admin enters a workspace through `account.workspace_access.grant`).
 *
 * AMENDED 2026-10-10 (ADR 0052; owner decision of 2026-10-10): `membership.role_change` and `membership.remove` join
 * the bridge — the organization's Owner and Admin change roles in and remove people from every workspace (and withdraw
 * its invitations, judged as `membership.remove`), within {@link workspaceMembershipChangeOf}'s limits. Inviting
 * (`membership.invite`) stays the workspace's own.
 */
export const WORKSPACE_CAPABILITY_BY_ACCOUNT_ROLE = Object.freeze({
    "membership.read": ["owner", "admin"],
    "membership.role_change": ["owner", "admin"],
    "membership.remove": ["owner", "admin"],
    "workspace.lifecycle": ["owner", "admin"],
});
for (const [capability, roles] of Object.entries(WORKSPACE_CAPABILITY_BY_ACCOUNT_ROLE)) {
    if (!WORKSPACE_CAPABILITY_SET.has(capability)) {
        throw new Error(`WORKSPACE_CAPABILITY_BY_ACCOUNT_ROLE: ${capability} is not a workspace capability`);
    }
    for (const role of roles ?? []) {
        if (!ACCOUNT_ROLE.includes(role)) {
            throw new Error(`WORKSPACE_CAPABILITY_BY_ACCOUNT_ROLE: ${capability} names ${role}, which is not an account role`);
        }
    }
}
/** Whether an account role holds a workspace capability on every workspace of its account (ADR 0041). */
export function accountRoleHoldsWorkspaceCapability(role, capability) {
    return WORKSPACE_CAPABILITY_BY_ACCOUNT_ROLE[capability]?.includes(role) === true;
}
/** The account roles holding a workspace capability on every workspace, in `ACCOUNT_ROLE` order (ADR 0041). */
export function accountRolesHoldingWorkspaceCapability(capability) {
    return ACCOUNT_ROLE.filter((role) => accountRoleHoldsWorkspaceCapability(role, capability));
}
/** Both maps together — what `./auth-context.ts`'s listing (A2) reads. */
export const CAPABILITY_MATRIX = Object.freeze({
    account: ACCOUNT_CAPABILITY_ROLE,
    workspace: WORKSPACE_CAPABILITY_ROLE,
});
// Every cell names a role of its own set, or the module refuses to load.
for (const [capability, roles] of Object.entries(ACCOUNT_CAPABILITY_ROLE)) {
    for (const role of roles) {
        if (!ACCOUNT_ROLE.includes(role)) {
            throw new Error(`ACCOUNT_CAPABILITY_ROLE: ${capability} names ${role}, which is not an account role`);
        }
    }
}
for (const [capability, roles] of Object.entries(WORKSPACE_CAPABILITY_ROLE)) {
    for (const role of roles) {
        if (!MEMBER_ROLE.includes(role)) {
            throw new Error(`WORKSPACE_CAPABILITY_ROLE: ${capability} names ${role}, which is not a member role`);
        }
    }
}
const ACCOUNT_HELD = new Map(ACCOUNT_ROLE.map((role) => [role, new Set(ACCOUNT_CAPABILITY.filter((one) => ACCOUNT_CAPABILITY_ROLE[one].includes(role)))]));
const WORKSPACE_HELD = new Map(MEMBER_ROLE.map((role) => [role, new Set(WORKSPACE_CAPABILITY.filter((one) => WORKSPACE_CAPABILITY_ROLE[one].includes(role)))]));
/** Whether an account role holds an account capability. */
export function holdsAccountCapability(role, capability) {
    return ACCOUNT_HELD.get(role)?.has(capability) === true;
}
/** Whether a member role holds a workspace capability. */
export function holdsWorkspaceCapability(role, capability) {
    return WORKSPACE_HELD.get(role)?.has(capability) === true;
}
/** The account roles holding a capability, in `ACCOUNT_ROLE` order. */
export function accountRolesHolding(capability) {
    return ACCOUNT_ROLE.filter((role) => holdsAccountCapability(role, capability));
}
/** The member roles holding a capability, in `MEMBER_ROLE` order. */
export function workspaceRolesHolding(capability) {
    return MEMBER_ROLE.filter((role) => holdsWorkspaceCapability(role, capability));
}
/** Every workspace capability a member role holds, in the matrix's order. */
export function workspaceCapabilityOf(role) {
    return WORKSPACE_CAPABILITY.filter((one) => holdsWorkspaceCapability(role, one));
}
/** Every account capability an account role holds, in the matrix's order. */
export function accountCapabilityOf(role) {
    return ACCOUNT_CAPABILITY.filter((one) => holdsAccountCapability(role, one));
}
// ---------------------------------------------------------------------------------------
// Step-up, read/mutate, and the two deny lists
// ---------------------------------------------------------------------------------------
/** Capabilities that need a fresh step-up (fixture `step_up`). */
export const STEP_UP_CAPABILITY = [
    "account.ownership.transfer",
    "account.close",
    "account.sign_in_policy.write",
];
const STEP_UP_SET = new Set(STEP_UP_CAPABILITY);
/** Whether a capability needs a fresh step-up. */
export function needsStepUp(capability) {
    return STEP_UP_SET.has(capability);
}
/**
 * Whether a capability changes something. A read-only impersonation (an operator whose
 * `permission_level='read'`) is refused every one that does (`operator_read_only`).
 *
 * The reads are the names ending `.read` or `_read` (`account.audit.financial_read`). An export or
 * a playback is NOT a read here: it hands content out of the platform, so a read-only operator
 * is refused it too — the conservative side of an act that is not a plain read.
 */
export function capabilityMutates(capability) {
    return !/[._]read$/.test(capability);
}
/**
 * The capabilities an impersonated auth_session may never exercise, whatever the subject holds
 * (design §27.5.5, fixture `impersonation_denied`) — design §8.7's "no re-delegation", as a closed
 * list. Every mutating capability plan-014 added that touches people, keys, money, policy or
 * lifecycle is on it; operators change customer billing only through the audited operator API.
 */
export const IMPERSONATION_DENIED_CAPABILITY = [
    "account_membership.invite",
    "account_membership.role_change",
    "account_membership.remove",
    "account.ownership.transfer",
    "account.close",
    "account.sign_in_policy.write",
    "account.api_key.manage",
    "account.finance.manage",
    "account.billing_delegate.manage",
    "account.workspace_access.grant",
    "account.workspace.create",
    "membership.invite",
    "membership.role_change",
    "membership.remove",
    "api_key.manage",
    "api_key.integration_manage",
    "webhook_endpoint.manage",
    "resource_share.publish",
    "resource_share.accept",
    "spend_cap.write",
    "workspace.lifecycle",
    "do_not_call_entry.remove",
    "compliance.operator_access",
    "compliance.consent_line",
    "person_data_request.create",
];
const IMPERSONATION_DENIED_SET = new Set(IMPERSONATION_DENIED_CAPABILITY);
/** Design §8.7 / §27.5.5: an impersonated auth_session may not re-delegate. */
export function deniedUnderImpersonation(capability) {
    return IMPERSONATION_DENIED_SET.has(capability);
}
/** Capabilities no API key ever holds, whatever its scopes (fixture `never_by_api_key`, design §27.5.4). */
export const NEVER_BY_API_KEY_CAPABILITY = [
    "account_membership.read",
    "account_membership.invite",
    "account_membership.role_change",
    "account_membership.remove",
    "membership.read",
    "membership.invite",
    "membership.role_change",
    "membership.remove",
    "account.ownership.transfer",
    "account.close",
    "account.sign_in_policy.write",
    "account.security.read",
    "account.finance.manage",
    "account.billing_delegate.manage",
    "account.api_key.manage",
    "api_key.manage",
    "api_key.integration_manage",
    "account.audit.read",
    "account.audit.financial_read",
    "audit_entry.read",
];
const NEVER_BY_API_KEY_SET = new Set(NEVER_BY_API_KEY_CAPABILITY);
const NEVER_BY_API_KEY_NAME = NEVER_BY_API_KEY_SET;
/**
 * Every scope string a key may carry (copy, unchanged — the strings are the API's) and what it
 * grants. `credit:spend`, `order:read` and `order:write` keep their existing route semantics and
 * map to no named capability. `billing:read`, `usage:read` and `document:read` are the account-scope
 * strings C3 issues (subsets of `account.finance.read`).
 */
export const API_KEY_SCOPE_GRANT = Object.freeze({
    "agent:read": { scopeKind: "workspace", capability: ["voice_agent.read", "agent_document.read"] },
    "agent:write": { scopeKind: "workspace", capability: ["voice_agent.write", "agent_document.write"] },
    "conversation:read": { scopeKind: "workspace", capability: ["conversation.read", "transcript.read"] },
    "conversation:dial": { scopeKind: "workspace", capability: ["conversation.dial"] },
    "recording:read": { scopeKind: "workspace", capability: ["recording.play"] },
    "campaign:write": { scopeKind: "workspace", capability: ["campaign.operate", "contact_list.manage"] },
    "credit:spend": { scopeKind: "workspace", capability: [] },
    "order:read": { scopeKind: "workspace", capability: [] },
    "order:write": { scopeKind: "workspace", capability: [] },
    "billing:read": { scopeKind: "account", capability: ["account.finance.read"] },
    "usage:read": { scopeKind: "account", capability: ["account.finance.read"] },
    "document:read": { scopeKind: "account", capability: ["account.finance.read"] },
});
/** Whether a value is a scope string the capability map knows. */
export function isCapabilityApiKeyScope(value) {
    return typeof value === "string" && Object.hasOwn(API_KEY_SCOPE_GRANT, value);
}
/**
 * The capabilities a set of scope strings grants — never one on the `never_by_api_key` list.
 * An unknown string grants nothing (it is not guessed at).
 */
export function apiKeyCapabilityOf(scope) {
    const held = new Set();
    for (const one of scope) {
        if (!isCapabilityApiKeyScope(one))
            continue;
        for (const capability of API_KEY_SCOPE_GRANT[one].capability) {
            if (NEVER_BY_API_KEY_NAME.has(capability))
                continue;
            held.add(capability);
        }
    }
    return held;
}
/**
 * Whether a workspace caller may invite someone as `toRole` (fixture `membership.invite`:
 * an Admin invites non-owner roles only; only an Owner makes an Owner — review 2026-09-25 #132).
 */
export function memberInviteRefusal(caller, toRole) {
    if (caller === null || !holdsWorkspaceCapability(caller, "membership.invite"))
        return "capability_missing";
    if (toRole === "owner" && caller !== "owner")
        return "owner_only";
    return null;
}
/**
 * Whether a workspace caller may change a member holding `targetRole` to `toRole` (fixture
 * `membership.role_change`: an Admin changes non-owner targets only and never promotes anyone —
 * themselves included — to Owner). The last-Owner rule is the caller's (counted under a lock).
 */
export function memberRoleChangeRefusal(caller, targetRole, toRole) {
    if (caller === null || !holdsWorkspaceCapability(caller, "membership.role_change"))
        return "capability_missing";
    if ((targetRole === "owner" || toRole === "owner") && caller !== "owner")
        return "owner_only";
    return null;
}
/** Whether a workspace caller may remove a member holding `targetRole` (fixture `membership.remove`). */
export function memberRemoveRefusal(caller, targetRole) {
    if (caller === null || !holdsWorkspaceCapability(caller, "membership.remove"))
        return "capability_missing";
    if (targetRole === "owner" && caller !== "owner")
        return "owner_only";
    return null;
}
/** The roles a workspace caller may give someone — every role for an Owner, every non-owner role for an Admin. */
export function assignableMemberRole(caller) {
    if (caller === null || !holdsWorkspaceCapability(caller, "membership.role_change"))
        return [];
    return caller === "owner" ? MEMBER_ROLE : MEMBER_ROLE.filter((one) => one !== "owner");
}
/**
 * ADDED 2026-10-10 (ADR 0052): the limits of changing a workspace's membership by the ORGANIZATION's authority (fixture
 * `organization_authority_limits`) — restated here, held equal to the fixture by `capability.test.ts`:
 *
 *   · the organization's Owner and Admin act with a workspace Owner's limits (they may make, change and remove an Owner);
 *   · they MAY demote or remove the workspace's last Owner — the organization still oversees it (the workspace's own
 *     members keep the last-Owner rule);
 *   · nobody changes their OWN role by the organization's authority (no self-promotion through the bridge);
 *   · the organization's Admin never changes or removes a person who is the organization's Owner;
 *   · withdrawing an invitation is judged as `membership.remove`.
 */
export const ORGANIZATION_AUTHORITY_LIMIT = Object.freeze({
    "membership.role_change": "workspace_owner_limits_last_owner_allowed_never_self_admin_never_an_organization_owner",
    "membership.remove": "workspace_owner_limits_last_owner_allowed_admin_never_an_organization_owner",
    "invitation.withdraw": "by_membership_remove",
});
/** The organization's half of {@link workspaceMembershipChangeOf} (fixture `organization_authority_limits`). */
function organizationMembershipRefusal(question) {
    const { act, accountRole } = question;
    if (accountRole === null || !accountRoleHoldsWorkspaceCapability(accountRole, act))
        return "capability_missing";
    if (act === "membership.role_change" && question.self)
        return "owner_only";
    if (accountRole !== "owner" && question.targetIsOrganizationOwner)
        return "owner_only";
    return null;
}
/**
 * ADDED 2026-10-10 (ADR 0052): may this caller change or remove this membership, and by whose authority? The workspace
 * role's limits first (fixture `admin_limits`; the last-Owner rule holds for them), then the organization's
 * ({@link ORGANIZATION_AUTHORITY_LIMIT}): a change that would leave the workspace with no Owner is allowed only by the
 * organization's authority. Pure: the API asks it under its lock, the console asks it to decide what to offer.
 */
export function workspaceMembershipChangeOf(question) {
    const { act, memberRole, targetRole, toRole } = question;
    const byWorkspace = act === "membership.remove" ? memberRemoveRefusal(memberRole, targetRole) : memberRoleChangeRefusal(memberRole, targetRole, toRole);
    const byOrganization = organizationMembershipRefusal(question);
    if (question.lastOwnerAtStake) {
        if (byOrganization === null)
            return { allowed: true, authority: "organization" };
        return { allowed: false, code: byWorkspace === null ? "last_owner" : byWorkspace };
    }
    if (byWorkspace === null)
        return { allowed: true, authority: "workspace" };
    if (byOrganization === null)
        return { allowed: true, authority: "organization" };
    return { allowed: false, code: byWorkspace === "owner_only" || byOrganization === "owner_only" ? "owner_only" : "capability_missing" };
}
/**
 * The roles a caller may give someone in a workspace (ADR 0052): a workspace Owner's or the organization's Owner's and
 * Admin's every role, a workspace Admin's every non-owner role — the union of the two authorities.
 */
export function assignableWorkspaceRole(memberRole, accountRole) {
    if (accountRole !== null && accountRoleHoldsWorkspaceCapability(accountRole, "membership.role_change"))
        return MEMBER_ROLE;
    return assignableMemberRole(memberRole);
}
/** Whether `role` is the strongest workspace role — the one only itself may grant, change or remove. */
export function isOwnerRole(role) {
    return role === "owner";
}
/**
 * Whether an account caller may invite someone as `toRole` (fixture `account_membership.invite`:
 * an Admin invites non-owner roles only; nobody invites as Owner — ownership is transferred).
 */
export function accountInviteRefusal(caller, toRole) {
    if (caller === null || !holdsAccountCapability(caller, "account_membership.invite"))
        return "capability_missing";
    if (toRole === "owner")
        return "owner_only";
    return null;
}
/**
 * Whether an account caller may change a membership holding `targetRole` to `toRole` (fixture
 * `account_membership.role_change`: an Admin changes non-owner targets only, never to Owner and
 * never promotes themselves). Ownership moves only by transfer (`account.ownership.transfer`), so
 * no one is changed TO Owner here; an Owner may change another Owner (the last-Owner rule is the
 * caller's, `lastOwnerRefusalOf`).
 */
export function accountRoleChangeRefusal(input) {
    const { caller, targetRole, toRole, self } = input;
    if (caller === null || !holdsAccountCapability(caller, "account_membership.role_change"))
        return "capability_missing";
    if (toRole === "owner")
        return "owner_only";
    if (caller === "owner")
        return null;
    if (targetRole === "owner")
        return "owner_only";
    if (self && ACCOUNT_ROLE.indexOf(toRole) < ACCOUNT_ROLE.indexOf(targetRole))
        return "owner_only";
    return null;
}
/** Whether an account caller may remove a membership holding `targetRole` (an Admin never removes an Owner). */
export function accountRemoveRefusal(caller, targetRole) {
    if (caller === null || !holdsAccountCapability(caller, "account_membership.remove"))
        return "capability_missing";
    if (targetRole === "owner" && caller !== "owner")
        return "owner_only";
    return null;
}
/** No grants at all — what a person with no membership anywhere in the target holds. */
export const NO_GRANTS = Object.freeze({ accountRole: null, memberRole: null, delegatedBillingAccountId: [] });
const ALLOWED = Object.freeze({ allowed: true });
function refused(code) {
    return { allowed: false, code };
}
/** The workspace capabilities a draining or archived workspace still allows when mutating. */
const LIFECYCLE_CAPABILITY = new Set([
    "workspace.lifecycle",
    "account.workspace.manage",
]);
/** What a suspended account still allows: its money (to recover), and enough to render the pages. */
const SUSPENDED_ALLOWED = new Set([
    "account.finance.read",
    "account.finance.manage",
    "account.profile.read",
    "account.workspace_metadata.read",
    "billing.read",
]);
/** What a closed account still allows: reading its financial records (retained ten years, OD-19). */
const CLOSED_ALLOWED = new Set(["account.finance.read"]);
function accountOfTarget(target) {
    return target.accountId;
}
/** Step 1 — the principal's own rules: impersonation never crosses its grant; a key never exceeds its scopes. */
function principalRule(question) {
    const { principal, capability, target } = question;
    if (principal.kind === "impersonation") {
        if (accountOfTarget(target) !== principal.grantAccountId)
            return refused("impersonation_forbidden");
        if (deniedUnderImpersonation(capability))
            return refused("impersonation_forbidden");
        if (principal.readOnly && capabilityMutates(capability))
            return refused("operator_read_only");
        return ALLOWED;
    }
    if (principal.kind === "api_key") {
        if (accountOfTarget(target) !== principal.accountId)
            return refused("scope_insufficient");
        if (principal.scopeKind === "workspace") {
            // A workspace key serves its own workspace and nothing above or beside it.
            if (target.kind !== "workspace" || target.workspaceId !== principal.workspaceId)
                return refused("scope_insufficient");
        }
        if (NEVER_BY_API_KEY_SET.has(capability))
            return refused("scope_insufficient");
        const held = apiKeyCapabilityOf(principal.scope.filter((one) => isCapabilityApiKeyScope(one) && API_KEY_SCOPE_GRANT[one].scopeKind === principal.scopeKind));
        if (!held.has(capability))
            return refused("scope_insufficient");
        return ALLOWED;
    }
    return ALLOWED;
}
/** Step 2 — the target's lifecycle. */
function stateRule(question) {
    const { capability, resourceState } = question;
    const mutates = capabilityMutates(capability);
    if (resourceState.accountState === "closed") {
        return CLOSED_ALLOWED.has(capability) ? ALLOWED : refused("account_closing");
    }
    if (resourceState.accountState === "suspended" && !SUSPENDED_ALLOWED.has(capability)) {
        return refused("account_suspended");
    }
    if (resourceState.accountState === "closing" && question.purchase === true)
        return refused("account_closing");
    if (mutates && isWorkspaceCapability(capability) && !LIFECYCLE_CAPABILITY.has(capability)) {
        if (resourceState.workspaceState === "draining")
            return refused("workspace_draining");
        if (resourceState.workspaceState === "archived")
            return refused("workspace_archived");
    }
    return ALLOWED;
}
/** Step 3 — the roles held over the target. A key owned by nobody is bounded by its scopes alone. */
function roleRule(question) {
    const { principal, grants, target, capability, resourceState } = question;
    if (principal.kind === "api_key" && principal.ownerPersonId === null)
        return ALLOWED;
    if (target.kind === "billing_account") {
        if (capability !== "account.finance.read" && capability !== "account.finance.manage") {
            return refused("capability_missing");
        }
        if (grants.accountRole !== null && holdsAccountCapability(grants.accountRole, capability))
            return ALLOWED;
        // DD14-15: a dedicated (workspace-scope) balance may also be acted on through a live delegate
        // row for exactly that billing account, by someone still in the workspace it funds. A shared
        // (account-scope) balance always needs account finance authority (FR12-AUTH-05).
        const delegated = target.billingAccountScope === "workspace" &&
            grants.memberRole !== null &&
            grants.delegatedBillingAccountId.includes(target.billingAccountId);
        return delegated ? ALLOWED : refused("capability_missing");
    }
    if (isAccountCapability(capability)) {
        if (grants.accountRole === null || !holdsAccountCapability(grants.accountRole, capability)) {
            return refused("capability_missing");
        }
        if (capability === "account.workspace.create" &&
            grants.accountRole !== "owner" &&
            resourceState.workspaceCreationPolicy === "owner_only") {
            return refused("capability_missing");
        }
        return ALLOWED;
    }
    // A workspace capability is held through a role IN the target workspace — or, AMENDED 2026-10-09 (ADR 0041), for
    // the few the account role holds on every workspace of its account (`WORKSPACE_CAPABILITY_BY_ACCOUNT_ROLE`).
    if (target.kind !== "workspace")
        return refused("capability_missing");
    if (grants.memberRole !== null && holdsWorkspaceCapability(grants.memberRole, capability))
        return ALLOWED;
    if (grants.accountRole !== null && accountRoleHoldsWorkspaceCapability(grants.accountRole, capability))
        return ALLOWED;
    return refused("capability_missing");
}
/** Step 4 — assurance: a fresh step-up where the capability needs one, and the target's sign-in policy. */
function assuranceRule(question) {
    const { principal, capability, now, stepUpWindowSecond, requiredPolicyRevision } = question;
    if (principal.kind !== "person")
        return ALLOWED;
    if (needsStepUp(capability)) {
        if (stepUpWindowSecond === null || !Number.isInteger(stepUpWindowSecond) || stepUpWindowSecond <= 0) {
            throw new RangeError(`stepUpWindowSecond must be a positive whole number for ${capability}, received ${String(stepUpWindowSecond)}`);
        }
        const at = principal.assurance.steppedUpAt;
        if (at === null || now.getTime() - at.getTime() > stepUpWindowSecond * 1000 || at.getTime() > now.getTime()) {
            return refused("step_up_required");
        }
    }
    if (requiredPolicyRevision !== null && principal.assurance.signInPolicyRevision !== requiredPolicyRevision) {
        return refused("assurance_required");
    }
    return ALLOWED;
}
/**
 * Evaluates one question, deny by default; the first failing check names the code (design
 * §27.5.1's order): (1) the principal's own rules, (2) the target's lifecycle, (3) the roles held
 * over the target, (4) assurance.
 */
export function evaluateCapability(question) {
    for (const rule of [principalRule, stateRule, roleRule, assuranceRule]) {
        const decision = rule(question);
        if (!decision.allowed)
            return decision;
    }
    return ALLOWED;
}
/** The evaluator as a port (design §27.10.1), for consumers that take one by injection. */
export const capabilityEvaluator = Object.freeze({ evaluate: evaluateCapability });
// ---------------------------------------------------------------------------------------
// Retired workspace capabilities (design §27.5.3) — the old names leave the union
// ---------------------------------------------------------------------------------------
/**
 * Where each retired workspace capability went (fixture `retired_workspace_capability`). Nothing
 * asks the evaluator for a retired name; this map documents the rewrite and is pinned by a test.
 */
export const RETIRED_WORKSPACE_CAPABILITY = Object.freeze({
    "billing.write": ["spend_cap.write", "account.finance.manage"],
    "workspace.create": ["account.workspace.create"],
    "workspace.delete": ["workspace.lifecycle", "account.workspace.manage"],
    "sso.read": ["account.security.read"],
    "sso.write": ["account.sign_in_policy.write"],
});
//# sourceMappingURL=capability.js.map